/**
 * « Signaler » un message ou une personne (migration 0604, App Review 1.2) :
 * motif, précisions facultatives, et « bloquer aussi » dans le même geste.
 * L'équipe JeRaisonne (admins) est prévenue et traite sous 24 heures.
 */

import { useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { Button, cn } from '@jeraisonne/ui';
import { Sheet } from '@/features/evaluations/sheet';
import {
  REPORT_DETAILS_MAX,
  REPORT_REASON_LABELS,
  useReportContent,
  type ReportReason,
  type ReportTarget,
} from '@/lib/safety/user-safety';

export function ReportSheet({
  target,
  targetName,
  excerpt,
  alreadyBlocked = false,
  onClose,
}: {
  target: ReportTarget;
  /** Prénom / nom de la personne signalée (ou auteur du message). */
  targetName: string;
  /** Texte du message signalé, rappelé en tête. */
  excerpt?: string;
  /** Déjà bloquée : la case « bloquer aussi » est masquée. */
  alreadyBlocked?: boolean;
  onClose: () => void;
}) {
  const report = useReportContent();
  const [reason, setReason] = useState<ReportReason | null>(null);
  const [details, setDetails] = useState('');
  const [block, setBlock] = useState(false);
  const [touched, setTouched] = useState(false);
  const isMessage = target.kind !== 'user';

  async function send() {
    setTouched(true);
    if (!reason) return;
    try {
      await report.mutateAsync({ target, reason, details, block: block && !alreadyBlocked });
    } catch {
      /* affiché via report.error */
    }
  }

  if (report.isSuccess) {
    const blocked = block && !alreadyBlocked;
    return (
      <Sheet
        title="Merci"
        onClose={onClose}
        footer={
          <Button fullWidth onClick={onClose}>
            Fermer
          </Button>
        }
      >
        <div className="flex flex-col items-center gap-3 py-6 text-center">
          <CheckCircle2 className="h-12 w-12 text-green-500" aria-hidden />
          <p className="text-ink-900 font-semibold">Ton signalement a bien été envoyé.</p>
          <p className="text-ink-500 text-sm">
            L’équipe JeRaisonne le traite sous 24 heures. Un contenu contraire aux règles est retiré
            et son auteur peut être exclu.
          </p>
          {blocked && (
            <p className="text-ink-700 text-sm font-medium">
              {targetName} est bloqué : vous ne pouvez plus vous écrire.
            </p>
          )}
        </div>
      </Sheet>
    );
  }

  return (
    <Sheet
      title={isMessage ? 'Signaler ce message' : `Signaler ${targetName}`}
      onClose={onClose}
      footer={
        <Button fullWidth onClick={() => void send()} disabled={report.isPending}>
          {report.isPending ? 'Envoi…' : 'Envoyer le signalement'}
        </Button>
      }
    >
      {isMessage && excerpt && (
        <blockquote className="bg-ink-50 ocean:bg-white/5 text-ink-700 break-words rounded-xl px-3 py-2 text-sm">
          <span className="text-ink-400 mb-0.5 block text-xs font-semibold">{targetName}</span>
          {excerpt}
        </blockquote>
      )}

      <div role="radiogroup" aria-label="Motif du signalement" className="grid grid-cols-1 gap-2">
        {(Object.keys(REPORT_REASON_LABELS) as ReportReason[]).map((r) => (
          <button
            key={r}
            type="button"
            role="radio"
            aria-checked={reason === r}
            onClick={() => setReason(r)}
            className={cn(
              'min-h-[2.75rem] rounded-xl border px-3 py-2 text-left text-sm font-semibold active:scale-[0.98]',
              reason === r
                ? 'border-primary-500 bg-primary-50 text-primary-700 ocean:bg-primary-500/15 ocean:text-primary-200'
                : 'border-ink-200 text-ink-700 bg-white',
            )}
          >
            {REPORT_REASON_LABELS[r]}
          </button>
        ))}
      </div>
      {touched && !reason && (
        <p role="alert" className="text-sm text-red-600">
          Choisis un motif.
        </p>
      )}

      <label className="block">
        <span className="text-ink-700 mb-1 block text-sm font-semibold">
          Précisions <span className="text-ink-400 font-normal">(facultatif)</span>
        </span>
        <textarea
          value={details}
          onChange={(e) => setDetails(e.target.value)}
          maxLength={REPORT_DETAILS_MAX}
          rows={3}
          placeholder="Ce qui s’est passé, en quelques mots."
          className="border-ink-200 text-ink-900 w-full rounded-xl border bg-white px-3 py-2 text-sm"
        />
      </label>

      {!alreadyBlocked && (
        <label className="border-ink-200 flex min-h-[2.75rem] cursor-pointer items-start gap-3 rounded-xl border bg-white px-3 py-2.5">
          <input
            type="checkbox"
            checked={block}
            onChange={(e) => setBlock(e.target.checked)}
            className="accent-primary-600 mt-0.5 size-5 shrink-0"
          />
          <span className="text-sm">
            <span className="text-ink-900 block font-semibold">Bloquer aussi {targetName}</span>
            <span className="text-ink-500 block text-xs">
              Vous ne pourrez plus vous écrire ni vous ajouter.
            </span>
          </span>
        </label>
      )}

      {report.isError && (
        <p role="alert" className="text-sm text-red-600">
          {report.error instanceof Error ? report.error.message : 'Erreur inconnue.'}
        </p>
      )}
    </Sheet>
  );
}
